import axios from "axios";

import { baseUrl } from "../api";
import store from "../app/store";

/**
 * GS1 unwrapping used by the knowledge pricing pipeline before calling
 * `/barcode-finder/resolve` (the gateway endpoint does not unwrap GS1 itself).
 */
export function normalizeBarcode(raw) {
  if (!raw) {
    return raw;
  }

  const stripped = String(raw).replace(/\s+/g, "");

  if (stripped.includes("(01)")) {
    const match = /\(01\)(\d{14})/.exec(stripped);
    if (match) {
      return match[1];
    }
  }

  if (/^01\d{15,}/.test(stripped)) {
    return stripped.slice(2, 16);
  }

  if (stripped.startsWith("1002") && stripped.includes("3922")) {
    const idx3922 = stripped.indexOf("3922");
    return stripped.slice(4, idx3922);
  }

  return stripped;
}

function getPortalToken() {
  return store.getState().user?.access_token || "";
}

/**
 * Live lookup against Gateway `POST /barcode-finder/resolve`.
 * Auth: logged-in portal user token (`REACT_APP_API_URL` host).
 */
export async function resolveBarcodeFinder({
  rawBarcode,
  retailerId = null,
  includeAllBarcodes = true,
} = {}) {
  const token = getPortalToken();
  if (!token) {
    throw new Error("You need to be signed in to look up barcodes.");
  }

  const trimmed = String(rawBarcode || "").trim();
  if (!trimmed) {
    throw new Error("No barcode is available to look up.");
  }

  const normalized = normalizeBarcode(trimmed);
  const gatewayBase = (process.env.REACT_APP_API_URL || baseUrl || "").replace(/\/$/, "");

  if (!gatewayBase) {
    throw new Error("Gateway API URL is not configured (REACT_APP_API_URL).");
  }

  try {
    // Portal axios request interceptor already attaches `access_token` as a query
    // param — do not also send Authorization Bearer (Directus rejects dual auth).
    const res = await axios.post(
      `${gatewayBase}/barcode-finder/resolve`,
      {
        raw_barcode: normalized,
        retailer_id: retailerId || null,
        include_all_barcodes: Boolean(includeAllBarcodes),
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
        timeout: 30000,
      }
    );

    const data = res?.data?.data || {};
    return {
      queryBarcode: trimmed,
      normalizedBarcode: data.normalized_barcode || normalized,
      matchStrategy: data.match_strategy || "",
      status: data.status || "not_found",
      matches: Array.isArray(data.matches)
        ? data.matches.map((match) => ({
            barcodeId: match.barcode_id || "",
            barcodeCode: match.barcode_code || "",
            matchQuality: match.match_quality || "exact",
            beefProductId: match.beef_product_id || "",
            productName: match.product_name || "Unknown product",
            retailer: {
              id: match.retailer?.id || "",
              name: match.retailer?.name || "",
              logo: match.retailer?.logo || null,
            },
          }))
        : [],
    };
  } catch (error) {
    const status = error?.response?.status;
    const apiMessage = error?.response?.data?.errors?.[0]?.message;
    if (status === 401 || status === 403) {
      throw new Error("You do not have permission to look up barcodes.");
    }
    throw new Error(apiMessage || error.message || "Barcode lookup failed.");
  }
}
